import { BacktestResult, PersonasArtifact, Snapshot, type ScoredCandidate } from "@smb/shared";
import { useEffect, useState } from "react";

export interface LoadState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
}

/**
 * Loads and validates an engine artifact.
 *
 * Parsed through the same zod schema the engine writes with, so a snapshot
 * whose shape has drifted fails visibly here rather than rendering wrong
 * numbers in a chart. A stale artifact is a much more likely failure than a
 * network one, and it is the one that looks fine.
 */
function useArtifact<T>(url: string, schema: { parse: (v: unknown) => T }): LoadState<T> {
  const [state, setState] = useState<LoadState<T>>({ data: null, error: null, loading: true });

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const parsed = schema.parse(await res.json());
        if (!cancelled) setState({ data: parsed, error: null, loading: false });
      } catch (err) {
        if (!cancelled) {
          setState({
            data: null,
            error: err instanceof Error ? err.message : String(err),
            loading: false,
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [url]);

  return state;
}

export const useSnapshot = () => useArtifact("/data/snapshot.json", Snapshot);
export const useBacktest = () => useArtifact("/data/backtest.json", BacktestResult);
export const usePersonas = () => useArtifact("/data/personas.json", PersonasArtifact);

// --- formatting -------------------------------------------------------------

export const fmtPct = (v: number | null, digits = 1): string =>
  v === null ? "—" : `${(v * 100).toFixed(digits)}%`;

/** Values already expressed in percent by the provider (margins, ROE). */
export const fmtPctPoints = (v: number | null, digits = 1): string =>
  v === null ? "—" : `${v.toFixed(digits)}%`;

export const fmtRatio = (v: number | null, digits = 2): string =>
  v === null ? "—" : v.toFixed(digits);

export const fmtUsd = (v: number | null): string => {
  if (v === null) return "—";
  if (Math.abs(v) >= 1e12) return `$${(v / 1e12).toFixed(1)}T`;
  if (Math.abs(v) >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return `$${v.toFixed(2)}`;
};

/**
 * A share price, in full.
 *
 * Separate from `fmtUsd` because that one abbreviates above $1,000 — right for
 * a market cap, wrong for a quote, where it turns $1,204.30 into "$1.2K" and
 * throws away the digits a price is read for.
 */
export const fmtPrice = (v: number | null): string => (v === null ? "—" : `$${v.toFixed(2)}`);

/** A signed move against a reference, as a percentage. */
export const fmtDelta = (from: number | null, to: number | null): string => {
  if (from === null || to === null || from <= 0) return "—";
  const pct = (to / from - 1) * 100;
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(2)}%`;
};

/** Local wall-clock time of an instant, for labelling how fresh a quote is. */
export const fmtClock = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
};

export function fmtFactor(value: number | null, unit: string): string {
  if (value === null) return "—";
  switch (unit) {
    case "percent":
      // Price-derived factors are fractions; provider ratios are already
      // percentages. Magnitude is the only available discriminator, and
      // getting it wrong renders a 32% margin as 3200%.
      return Math.abs(value) <= 3 ? fmtPct(value) : fmtPctPoints(value);
    case "currency":
      return fmtUsd(value);
    case "days":
      return `${value.toFixed(0)}d`;
    case "count":
      return value.toFixed(0);
    default:
      return fmtRatio(value);
  }
}

export const SECTOR_LABELS: Record<string, string> = {
  Energy: "Energy",
  Materials: "Materials",
  Industrials: "Industrials",
  ConsumerDiscretionary: "Consumer Discretionary",
  ConsumerStaples: "Consumer Staples",
  HealthCare: "Health Care",
  Financials: "Financials",
  InformationTechnology: "Information Technology",
  CommunicationServices: "Communication Services",
  Utilities: "Utilities",
  RealEstate: "Real Estate",
  Unknown: "Unclassified",
};

export const severityColor = (severity: string): string =>
  severity === "severe"
    ? "var(--status-critical)"
    : severity === "caution"
      ? "var(--status-warning)"
      : "var(--text-muted)";

/** Candidates whose composite rests on no measurable sleeve at all. */
export const isBlind = (c: ScoredCandidate): boolean =>
  c.sleeves.every((s) => s.id === "risk" || s.zScore === null);
